import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import {
  ChatCircleDots, ArrowsClockwise, CaretUp, CheckCircle, EyeSlash, Eye, ChatTeardropText,
} from "@phosphor-icons/react";
import { formatDateShortUK } from "@/pages/admin/_helpers";
import { isConferenceOver } from "@/lib/conference";

// Refresh every 15 s during the live event so admins see new questions as
// they arrive. Once the conference is over we stop polling entirely (a
// manual refresh-on-click is plenty).
const REFRESH_MS = 15000;

/**
 * Live, auto-refreshing preview of the questions submitted to the
 * Featured Q&A session. Designed for the Day-3 "Ask the Experts" panel
 * but works for any featured session.
 *
 * Embedded inside the Admin → Info tab (right under the featured-session selector).
 */
export default function FeaturedQAPreview({ sessionId }) {
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHidden, setShowHidden] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const [s, q] = await Promise.all([
        api.get(`/sessions/${sessionId}`),
        api.get(`/sessions/${sessionId}/questions`),
      ]);
      setSession(s.data);
      setQuestions(q.data || []);
      setUpdatedAt(new Date());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    setLoading(true);
    load();
    if (!sessionId || isConferenceOver()) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, sessionId]);

  const moderate = async (q, status) => {
    try {
      await api.patch(`/questions/${q.id}`, { status });
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!sessionId) return null;
  if (loading && !session) {
    return <div className="text-center text-[#7A828C] py-6 text-sm">Loading preview…</div>;
  }

  const visible = questions
    .filter((q) => showHidden || q.status !== "hidden")
    .sort((a, b) => {
      // Answered questions drop to the bottom; otherwise sort by upvotes desc, then newest first.
      const aA = a.status === "answered" ? 1 : 0;
      const bA = b.status === "answered" ? 1 : 0;
      if (aA !== bA) return aA - bA;
      const dv = (b.upvote_count || 0) - (a.upvote_count || 0);
      if (dv) return dv;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  const totals = {
    all: questions.length,
    approved: questions.filter((q) => q.status === "approved").length,
    answered: questions.filter((q) => q.status === "answered").length,
    hidden: questions.filter((q) => q.status === "hidden").length,
  };

  return (
    <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid="featured-qa-preview">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ChatCircleDots size={16} weight="duotone" className="text-[#000000]" />
            <h3 className="font-display text-base">Questions for this session</h3>
          </div>
          {session && (
            <p className="text-xs text-[#7A828C] mt-0.5">
              {formatDateShortUK(session.date)} · {session.start_time} · {session.stage_label || session.stage} · {session.title}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-sm flex-shrink-0" data-testid="featured-qa-refresh">
          <ArrowsClockwise size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center mb-4">
        <Pill label="Total" value={totals.all} accent="text-[#1c1c24]" />
        <Pill label="Pending" value={totals.approved} accent="text-[#000000]" />
        <Pill label="Answered" value={totals.answered} accent="text-[#7A828C]" />
        <Pill label="Hidden" value={totals.hidden} accent="text-[#DC4F48]" />
      </div>

      <div className="flex items-center justify-between text-xs text-[#7A828C] mb-2">
        <span>
          {updatedAt
            ? <>Auto-refreshes every {REFRESH_MS / 1000}s · last update {updatedAt.toLocaleTimeString()}</>
            : "-"}
        </span>
        <button
          type="button"
          onClick={() => setShowHidden((v) => !v)}
          className="font-mono-time uppercase tracking-widest text-[10px] text-[#000000] hover:underline"
          data-testid="featured-qa-toggle-hidden"
        >
          {showHidden ? "Hide hidden" : `Show hidden (${totals.hidden})`}
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#7A828C]" data-testid="featured-qa-empty">
          <ChatTeardropText size={28} weight="duotone" className="mx-auto mb-2 text-[#7A828C]/60" />
          No questions submitted yet. The preview refreshes automatically as delegates ask.
        </div>
      ) : (
        <ol className="space-y-2">
          {visible.map((q, i) => (
            <li
              key={q.id}
              className={
                "p-3 rounded-sm border flex items-start gap-3 " +
                (q.status === "answered" ? "bg-[#F5F6F8] border-[#E5E9F0] opacity-70" : "bg-[#FFFFFF] border-[#E5E9F0]")
              }
              data-testid={`featured-qa-q-${q.id}`}
            >
              <div className="flex-shrink-0 flex flex-col items-center w-12 pt-0.5">
                <CaretUp size={14} weight="fill" className="text-[#000000]" />
                <span className="font-mono-time text-sm font-semibold text-[#000000] leading-tight">{q.upvote_count || 0}</span>
                <span className="font-mono-time text-[10px] uppercase text-[#7A828C] tracking-widest leading-tight mt-0.5">#{i + 1}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className={"text-sm leading-snug " + (q.status === "answered" ? "line-through text-[#7A828C]" : "text-[#1c1c24]")}>
                  {q.text}
                </p>
                <div className="text-xs text-[#7A828C] mt-1">
                  {q.user_name || "Anonymous"} · {q.status}{q.created_at ? ` · ${new Date(q.created_at).toLocaleString()}` : ""}
                </div>
                <div className="flex gap-1.5 flex-wrap mt-2">
                  {q.status !== "answered" && (
                    <Button size="sm" variant="outline" onClick={() => moderate(q, "answered")} className="rounded-sm h-7 text-xs" data-testid={`featured-qa-${q.id}-answer`}>
                      <CheckCircle size={12} className="mr-1" /> Mark answered
                    </Button>
                  )}
                  {q.status !== "hidden" ? (
                    <Button size="sm" variant="outline" onClick={() => moderate(q, "hidden")} className="rounded-sm h-7 text-xs" data-testid={`featured-qa-${q.id}-hide`}>
                      <EyeSlash size={12} className="mr-1" /> Hide
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => moderate(q, "approved")} className="rounded-sm h-7 text-xs" data-testid={`featured-qa-${q.id}-unhide`}>
                      <Eye size={12} className="mr-1" /> Unhide
                    </Button>
                  )}
                  {q.status === "answered" && (
                    <Button size="sm" variant="outline" onClick={() => moderate(q, "approved")} className="rounded-sm h-7 text-xs" data-testid={`featured-qa-${q.id}-reopen`}>
                      <Eye size={12} className="mr-1" /> Re-open
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Pill({ label, value, accent }) {
  return (
    <div className="p-2 rounded-sm bg-[#F5F6F8] border border-[#E5E9F0]">
      <div className={"font-display text-xl " + accent}>{value}</div>
      <div className="text-[10px] font-mono-time uppercase tracking-widest text-[#7A828C]">{label}</div>
    </div>
  );
}
